import React, { useEffect, useRef, useState } from "react";
import { MDBCol, MDBRow } from "mdbreact";
import {  Input, Button } from "abs-ui-components";
import { InputLabel } from "@mui/material";
import { emitToasterMsg } from "../../../services/toasterUtility";
import { updateUKETSCompanyName } from "../../../services/UKETSAerService";
import "./UKETSCompanyDetails.scss";

const UKETSCompanyDetails = ({ ukEtsAerMstId, aerDetails, isEditable, onDetailsUpdated }) => {
	const [companyName, setCompanyName] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [companyNameError, setCompanyNameError] = useState("");
	const originalNameRef = useRef("");

	// Sync local state when aerDetails changes
	useEffect(() => {
		if (aerDetails) {
			setCompanyName(aerDetails.companyName || "");
			setCompanyNameError("");
			originalNameRef.current = aerDetails.companyName || "";
		}
	}, [aerDetails]);

	// Save company name on blur — only if value has changed and report is editable
	const handleCompanyNameBlur = async () => {
		const trimmed = companyName.trim();
		if (!isEditable) return;
		if (trimmed === originalNameRef.current) return;
		if (!trimmed) {
			setCompanyNameError("Company Name is required.");
			return;
		}
		setCompanyNameError("");

		setIsSaving(true);
		try {
			const result = await updateUKETSCompanyName({
				ukEtsCompanyLevelReportId: Number(ukEtsAerMstId),
				companyName: trimmed,
			});
			if (result?.isSuccess) {
				originalNameRef.current = trimmed;
				setCompanyNameError("");
				emitToasterMsg("success", "Saved", "Company name updated successfully.");
				onDetailsUpdated && onDetailsUpdated();
			} else {
				setCompanyName(originalNameRef.current);
				setCompanyNameError("");
				emitToasterMsg("error", "Save Failed", "Unable to update company name.");
			}
		} catch (err) {
			setCompanyName(originalNameRef.current);
			setCompanyNameError("");
			emitToasterMsg("error", "Error", err.message);
		} finally {
			setIsSaving(false);
		}
	};

	const ReadOnlyField = ({ value }) => <Input value={value || ""} disabled className="inputHeight" style={{ backgroundColor: "#f1f1f2" }} />;

	if (!aerDetails) return null;

	return (
		<div className="p-3">
			<div
				style={{
					backgroundColor: "#042a4c",
					color: "#fff",
					padding: "10px 16px",
					fontWeight: "450",
					fontSize: "16px",
					borderRadius: "2px 2px 0 0",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					marginBottom: "0",
				}}
			>
				<span>Company Details</span>
			</div>

			{/* Fields panel */}
			<div
				style={{
					border: "1px solid #e0e0e0",
					borderTop: "none",
					padding: "20px",
					backgroundColor: "#fff",
				}}
			>
				{/* Row 1: Company Name | Company IMO Number */}
				<MDBRow className="d-flex mb-4">
					<MDBCol md="3" className="my-auto">
						<InputLabel
							required={isEditable}
							sx={{
								fontSize: "14px",
								fontWeight: 500,
								color: "text.primary",
								mb: 1,
								"& .MuiFormLabel-asterisk": {
									color: "red",
								},
							}}
						>
							Maritime Operator / Company Name
						</InputLabel>
					</MDBCol>
					<MDBCol md="9">
						{isEditable ? (
							<>
								<Input
									value={companyName}
									onChange={(value) => {
										setCompanyName(value);
										if (companyNameError) setCompanyNameError("");
									}}
									onBlur={handleCompanyNameBlur}
									placeholder=""
									className="inputHeight"
									disabled={isSaving}
								/>
								{companyNameError && <div style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px" }}>{companyNameError}</div>}
							</>
						) : (
							<ReadOnlyField value={companyName} />
						)}
					</MDBCol>
				</MDBRow>
				<MDBRow className="d-flex mb-4">
					<MDBCol md="3" className="my-auto">
						<InputLabel
							sx={{
								fontSize: "14px",
								fontWeight: 500,
								color: "text.primary",
								mb: 1,
							}}
						>
							Maritime Operator / Company IMO Number
						</InputLabel>
					</MDBCol>
					<MDBCol md="9">
						<ReadOnlyField value={aerDetails.companyImoNumber} />
					</MDBCol>
					<MDBRow />
				</MDBRow>
				<MDBRow className="d-flex mb-4">
					<MDBCol md="3" className="my-auto">
						<InputLabel
							sx={{
								fontSize: "14px",
								fontWeight: 500,
								color: "text.primary",
								mb: 1,
							}}
						>
							WCN Name
						</InputLabel>
					</MDBCol>
					<MDBCol md="9">
						<ReadOnlyField value={aerDetails.wcnCompanyName} />
					</MDBCol>
				</MDBRow>

				<MDBRow className="d-flex mb-4">
					<MDBCol md="3" className="my-auto">
						<InputLabel
							sx={{
								fontSize: "14px",
								fontWeight: 500,
								color: "text.primary",
								mb: 1,
							}}
						>
							WCN Number
						</InputLabel>
					</MDBCol>
					<MDBCol md="9">
						<ReadOnlyField value={aerDetails.wcnNumber} />
					</MDBCol>
				</MDBRow>

				{aerDetails?.reasonForCreation?.trim() && (
					<MDBRow className="d-flex mb-2">
						<MDBCol md="3" className="my-auto">
							<InputLabel
								sx={{
									fontSize: "14px",
									fontWeight: 500,
									color: "text.primary",
									mb: 1,
								}}
							>
								Reason for Creating Duplicate UK ETS Company Level Report
							</InputLabel>
						</MDBCol>

						<MDBCol md="9">
							<Input
								value={aerDetails.reasonForCreation}
								disabled
								placeholder="Enter reason for creating duplicate report"
								className="inputHeight"
								style={{ backgroundColor: "#f1f1f2" }}
							/>
						</MDBCol>
					</MDBRow>
				)}
			</div>
		</div>
	);
};

export default UKETSCompanyDetails;
